import { useMemo } from "react"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { useGetAvailableSubnetworkCountForModule, useNetworksStateContext } from "@l2r-front/l2r-networks"

import { API_MODULE_ROADWORK, appRoad } from "../../../../common/constants/appRoad"
import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { RoadworksBudgetsKpiComponent } from "../../components/RoadworksBudgetsKpi"
import { STATUS_DONE } from "../../constants/status"
import { useRoadworksProject } from "../../hooks/queries/useRoadworksProjectQuery"
import { useYearsStatsQuery } from "../../hooks/queries/useYearsStatsQuery"

import * as Styled from "./RoadworksBudgetsKpi.styled"

export function RoadworksBudgetsKpi(props) {

    const { data, isLoading: isLoadingYearsStats, isError } = useYearsStatsQuery()
    const { t } = useTranslation()
    const { data: project, isLoading: isLoadingProject } = useRoadworksProject()
    const { allSubNetworksSelected, selectedSubNetworkSlugs } = useNetworksStateContext()
    const currentSubNetworksWithRoadworksCount = useGetAvailableSubnetworkCountForModule(API_MODULE_ROADWORK)

    const costDataLength = Object.entries(data?.cost || {})?.length
    const isLoading = isLoadingYearsStats || isLoadingProject

    const budgets = useMemo(() => {
        if (selectedSubNetworkSlugs?.length && !allSubNetworksSelected) {
            const subnetworksBudgets = project?.subnetworkBudgets?.filter(budget => selectedSubNetworkSlugs.includes(budget.network)) || []
            return subnetworksBudgets.reduce((acc, budget) => {
                Object.entries(budget.budgets).forEach(([year, amount]) => {
                    if (acc[year]) {
                        acc[year] += amount
                    } else {
                        acc[year] = amount
                    }
                })
                return acc
            }, {})
        } else {
            return project?.budgets
        }
    }, [project, allSubNetworksSelected, selectedSubNetworkSlugs])

    const formattedData = useMemo(() => {
        if (costDataLength && budgets) {
            return Object.entries(data.cost).map(([key, value]) => {
                const doneCost = parseFloat(value[STATUS_DONE])
                return ({
                    date: parseInt(key),
                    done: doneCost,
                    planned: budgets[key] ?? 0,
                })
            })
        }
        return []
    }, [costDataLength, data, budgets])

    if (isError) {
        return <Styled.KpiError
            {...props}
            title={t(I18N_NAMESPACE, "containers.notationKpi.error")}
            id="roadworks-budgets-kpi-error"
            message={t(I18N_NAMESPACE, "containers.notationKpi.error")}
            path={`${appRoad.path}/${appRoad.modules.roadworks.path}`}
        />
    }

    if (isLoading) {
        return <Styled.KpiSkeleton {...props} />
    }

    return <Styled.KpiCard
        id="roadworks-budgets-kpi"
        subNetworksCount={currentSubNetworksWithRoadworksCount}
        path={`${appRoad.path}/${appRoad.modules.roadworks.path}`}
        title={t(I18N_NAMESPACE, "components.roadworksBudgetsKpi.title", { currency: project?.currency?.symbol || "" })}
        {...props}
    >
        <Styled.ChartContainer>
            <RoadworksBudgetsKpiComponent currency={project?.currency?.symbol} data={formattedData} />
        </Styled.ChartContainer>
    </Styled.KpiCard >
}